import { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tooltip,
  Container,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  TableContainer,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useBoolean } from 'src/hooks/use-boolean';
import {
  useTable,
  TableHeadCustom,
  TableNoData,
  TableEmptyRows,
  emptyRows,
  TablePaginationCustom,
} from 'src/components/table';
import { fDateTime } from 'src/utils/format-time';
import {
  useTermsAndConditionsActions,
  useTermsAndConditionsData,
} from 'src/context/providers/TermsAndConditionsContext';
import { toast } from 'react-toastify';
import TcViewDialog from './TcViewDialog';
import TcCreateDrawer from './TcCreateDrawer';

const TABLE_HEAD = [
  { id: 'title', label: 'Title', width: 240 },
  { id: 'en', label: 'English' },
  { id: 'ka', label: 'Georgian' },
  { id: 'active', label: 'Status', width: 160 },
  { id: 'created_at', label: 'Created At', width: 180 },
  { id: '', width: 100 },
];

const truncate = (value, max = 80) => {
  if (!value) return '';
  const clean = String(value)
    .replace(/<(.|\n)*?>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
};

export default function TcTable() {
  const settings = useSettingsContext();
  const table = useTable({ defaultOrderBy: 'created_at', defaultOrder: 'desc' });

  const { items, loading, activating } = useTermsAndConditionsData() || {
    items: [],
    loading: false,
    activating: null,
  };
  const { createItem, activate, fetchOne } = useTermsAndConditionsActions() || {};

  const createDrawer = useBoolean();
  const viewDialog = useBoolean();

  const [selectedRow, setSelectedRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = useCallback(
    async (payload) => {
      if (!createItem) return;
      setSubmitting(true);
      const ok = await createItem(payload);
      setSubmitting(false);
      if (ok) {
        createDrawer.onFalse();
      }
    },
    [createItem, createDrawer]
  );

  const handleActivate = useCallback(
    (row) => {
      if (!activate || row.active) return;
      activate(row.id);
    },
    [activate]
  );

  const handleView = useCallback(
    async (row) => {
      try {
        setSelectedRow(await fetchOne(row.id));
        viewDialog.onTrue();
      } catch (error) {
        toast.error('Could not load terms and conditions');
      }
    },
    [viewDialog, fetchOne]
  );

  const handleCloseView = useCallback(() => {
    viewDialog.onFalse();
    setSelectedRow(null);
  }, [viewDialog]);

  const paginatedRows = useMemo(
    () =>
      items.slice(
        table.page * table.rowsPerPage,
        table.page * table.rowsPerPage + table.rowsPerPage
      ),
    [items, table.page, table.rowsPerPage]
  );

  const denseHeight = table.dense ? 52 : 72;
  const notFound = !loading && items.length === 0;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Terms and Conditions"
        action={
          <Button
            onClick={createDrawer.onTrue}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            New Terms and Conditions
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      {loading && <LinearProgress color="success" sx={{ mb: 2 }} />}

      <Card>
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headLabel={TABLE_HEAD}
                rowCount={items.length}
                onSort={table.onSort}
              />

              <TableBody>
                {paginatedRows.map((row) => {
                  const isActivating = activating === row.id;
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleView(row)}
                    >
                      <TableCell>
                        <Typography variant="subtitle2" noWrap>
                          {row.title}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {truncate(row.en)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {truncate(row.ka)}
                        </Typography>
                      </TableCell>

                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {row.active ? (
                          <Label variant="soft" color="success">
                            Active
                          </Label>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            disabled={isActivating}
                            startIcon={
                              isActivating ? (
                                <CircularProgress size={14} color="inherit" />
                              ) : (
                                <Iconify icon="solar:check-circle-bold" />
                              )
                            }
                            onClick={() => handleActivate(row)}
                          >
                            Activate
                          </Button>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {fDateTime(row.created_at)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View">
                          <IconButton onClick={() => handleView(row)}>
                            <Iconify icon="solar:eye-bold" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, items.length)}
                />

                <TableNoData notFound={notFound} />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={items.length}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>

      <TcCreateDrawer
        open={createDrawer.value}
        onClose={createDrawer.onFalse}
        onSubmit={handleCreate}
        submitting={submitting}
      />

      <TcViewDialog
        open={viewDialog.value}
        onClose={handleCloseView}
        row={selectedRow}
      />
    </Container>
  );
}

TcTable.propTypes = {};
